import { useEffect, useRef, useState } from "react";
import { X, Plus, Trash2, Loader2, Send } from "lucide-react";
import { enviarInterativo } from "../../services/mensagensApi";

const TAB_BOTOES = "button";
const TAB_LISTA  = "list";

function gerarId() {
  return Math.random().toString(36).slice(2, 10);
}

function Field({ label, required = false, children }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-ink-3">
        {label}
        {required && <span className="text-red-400 ml-0.5" aria-label="obrigatório">*</span>}
      </span>
      {children}
    </div>
  );
}

/**
 * Modal com 2 tabs: Botões (até 3 reply buttons) e Lista (até 10 rows em sections).
 * Constrói e envia mensagens interativas Meta WhatsApp.
 *
 * @param {{
 *   conversaId: number,
 *   onClose: () => void,
 *   onSent: (mensagem: object) => void,
 * }} props
 */
export default function InteractiveBuilder({ conversaId, onClose, onSent }) {
  const [tab,      setTab]      = useState(TAB_BOTOES);
  const [enviando, setEnviando] = useState(false);
  const [erro,     setErro]     = useState(null);
  const modalRef = useRef(null);

  // Estado tab Botões
  const [bodyBotoes,   setBodyBotoes]   = useState("");
  const [footerBotoes, setFooterBotoes] = useState("");
  const [botoes,       setBotoes]       = useState([{ id: gerarId(), title: "" }]);

  // Estado tab Lista
  const [bodyLista,    setBodyLista]    = useState("");
  const [footerLista,  setFooterLista]  = useState("");
  const [buttonLabel,  setButtonLabel]  = useState("");
  const [sections,     setSections]     = useState([
    { title: "", rows: [{ id: gerarId(), title: "", description: "" }] },
  ]);

  useEffect(() => { modalRef.current?.focus(); }, []);

  useEffect(() => {
    function handleEsc(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  // ── Botões helpers ──────────────────────────────────────────────────────────

  function adicionarBotao() {
    if (botoes.length >= 3) return;
    setBotoes((prev) => [...prev, { id: gerarId(), title: "" }]);
  }

  function removerBotao(idx) {
    setBotoes((prev) => prev.filter((_, i) => i !== idx));
  }

  function atualizarBotao(idx, value) {
    setBotoes((prev) => prev.map((b, i) => (i === idx ? { ...b, title: value } : b)));
  }

  // ── Lista helpers ───────────────────────────────────────────────────────────

  function adicionarSection() {
    setSections((prev) => [
      ...prev,
      { title: "", rows: [{ id: gerarId(), title: "", description: "" }] },
    ]);
  }

  function totalRows() {
    return sections.reduce((acc, s) => acc + s.rows.length, 0);
  }

  function adicionarRow(secIdx) {
    if (totalRows() >= 10) return;
    setSections((prev) =>
      prev.map((s, i) =>
        i !== secIdx ? s : { ...s, rows: [...s.rows, { id: gerarId(), title: "", description: "" }] }
      )
    );
  }

  function removerRow(secIdx, rowIdx) {
    setSections((prev) =>
      prev.map((s, i) =>
        i !== secIdx ? s : { ...s, rows: s.rows.filter((_, j) => j !== rowIdx) }
      )
    );
  }

  function atualizarSection(secIdx, value) {
    setSections((prev) => prev.map((s, i) => (i !== secIdx ? s : { ...s, title: value })));
  }

  function atualizarRow(secIdx, rowIdx, field, value) {
    setSections((prev) =>
      prev.map((s, i) =>
        i !== secIdx
          ? s
          : { ...s, rows: s.rows.map((r, j) => (j !== rowIdx ? r : { ...r, [field]: value })) }
      )
    );
  }

  // ── Validação e montagem de payload ────────────────────────────────────────

  function validarEMontar() {
    if (tab === TAB_BOTOES) {
      if (!bodyBotoes.trim()) return { erro: "Texto do corpo obrigatório." };
      if (botoes.length === 0) return { erro: "Adicione pelo menos 1 botão." };
      if (botoes.some((b) => !b.title.trim())) return { erro: "Todos os botões precisam de título." };

      const payload = {
        interactive_type: "button",
        body:   { text: bodyBotoes.trim() },
        action: {
          buttons: botoes.map((b) => ({
            type:  "reply",
            reply: { id: b.id, title: b.title.trim().slice(0, 20) },
          })),
        },
      };
      if (footerBotoes.trim()) payload.footer = { text: footerBotoes.trim() };
      return { payload };
    }

    // TAB_LISTA
    if (!bodyLista.trim())   return { erro: "Texto do corpo obrigatório." };
    if (!buttonLabel.trim()) return { erro: "Rótulo do botão obrigatório." };
    if (totalRows() === 0)   return { erro: "Adicione pelo menos 1 item." };
    if (sections.some((s) => s.rows.some((r) => !r.title.trim()))) {
      return { erro: "Todos os itens precisam de título." };
    }

    const payload = {
      interactive_type: "list",
      body:   { text: bodyLista.trim() },
      action: {
        button:   buttonLabel.trim().slice(0, 20),
        sections: sections.map((s) => ({
          title: s.title.trim().slice(0, 24),
          rows:  s.rows.map((r) => {
            const row = { id: r.id, title: r.title.trim().slice(0, 24) };
            if (r.description.trim()) row.description = r.description.trim().slice(0, 72);
            return row;
          }),
        })),
      },
    };
    if (footerLista.trim()) payload.footer = { text: footerLista.trim() };
    return { payload };
  }

  async function handleEnviar() {
    const { erro: validacaoErro, payload } = validarEMontar();
    if (validacaoErro) { setErro(validacaoErro); return; }

    setEnviando(true);
    setErro(null);
    try {
      const resultado = await enviarInterativo(conversaId, payload);
      onSent(resultado);
      onClose();
    } catch (err) {
      setErro(err?.response?.data?.error ?? "Erro ao enviar mensagem interativa.");
    } finally {
      setEnviando(false);
    }
  }

  const totalRowsAtual = totalRows();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Construir mensagem interativa"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className="bg-surface-1 border border-white/[0.08] rounded-xl shadow-2xl w-full max-w-md max-h-[88vh] flex flex-col outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06] flex-shrink-0">
          <h2 className="text-sm font-semibold text-ink-1">Mensagem interativa</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar modal"
            className="text-ink-4 hover:text-ink-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded p-0.5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/[0.06] flex-shrink-0" role="tablist">
          {[{ id: TAB_BOTOES, label: "Botões" }, { id: TAB_LISTA, label: "Lista" }].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => { setTab(t.id); setErro(null); }}
              className={`flex-1 py-2.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-inset focus-visible:ring-2 focus-visible:ring-brand-500 ${
                tab === t.id
                  ? "text-brand-400 border-b-2 border-brand-500"
                  : "text-ink-4 hover:text-ink-2"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-zinc-900 scrollbar-thumb-zinc-700 px-5 py-4 flex flex-col gap-4 min-h-0">

          {/* ── Tab Botões ── */}
          {tab === TAB_BOTOES && (
            <>
              <Field label="Texto do corpo" required>
                <textarea
                  value={bodyBotoes}
                  onChange={(e) => setBodyBotoes(e.target.value)}
                  maxLength={1024}
                  rows={3}
                  placeholder="Digite a mensagem..."
                  className="w-full bg-surface-2 border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-ink-2 placeholder:text-ink-5 resize-none focus:outline-none focus:border-brand-600 transition-colors"
                />
              </Field>

              <Field label="Rodapé (opcional)">
                <input
                  type="text"
                  value={footerBotoes}
                  onChange={(e) => setFooterBotoes(e.target.value)}
                  maxLength={60}
                  placeholder="Texto do rodapé"
                  className="w-full bg-surface-2 border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
                />
              </Field>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-ink-3 uppercase tracking-wide">
                    Botões ({botoes.length}/3)
                  </span>
                  {botoes.length < 3 && (
                    <button
                      type="button"
                      onClick={adicionarBotao}
                      aria-label="Adicionar botão"
                      className="flex items-center gap-1 text-xs text-brand-400 hover:text-brand-300 transition-colors focus:outline-none focus-visible:underline"
                    >
                      <Plus className="w-3 h-3" aria-hidden="true" /> Adicionar
                    </button>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {botoes.map((b, idx) => (
                    <div key={b.id} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={b.title}
                        onChange={(e) => atualizarBotao(idx, e.target.value)}
                        maxLength={20}
                        placeholder={`Botão ${idx + 1}`}
                        aria-label={`Título do botão ${idx + 1}`}
                        className="flex-1 bg-surface-2 border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
                      />
                      {botoes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removerBotao(idx)}
                          aria-label={`Remover botão ${idx + 1}`}
                          className="text-ink-5 hover:text-red-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded p-0.5"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── Tab Lista ── */}
          {tab === TAB_LISTA && (
            <>
              <Field label="Texto do corpo" required>
                <textarea
                  value={bodyLista}
                  onChange={(e) => setBodyLista(e.target.value)}
                  maxLength={1024}
                  rows={3}
                  placeholder="Digite a mensagem..."
                  className="w-full bg-surface-2 border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-ink-2 placeholder:text-ink-5 resize-none focus:outline-none focus:border-brand-600 transition-colors"
                />
              </Field>

              <Field label="Rótulo do botão da lista" required>
                <input
                  type="text"
                  value={buttonLabel}
                  onChange={(e) => setButtonLabel(e.target.value)}
                  maxLength={20}
                  placeholder="Ex: Ver opções"
                  className="w-full bg-surface-2 border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
                />
              </Field>

              <Field label="Rodapé (opcional)">
                <input
                  type="text"
                  value={footerLista}
                  onChange={(e) => setFooterLista(e.target.value)}
                  maxLength={60}
                  placeholder="Texto do rodapé"
                  className="w-full bg-surface-2 border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
                />
              </Field>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-ink-3 uppercase tracking-wide">
                    Seções — itens ({totalRowsAtual}/10)
                  </span>
                  <button
                    type="button"
                    onClick={adicionarSection}
                    aria-label="Adicionar seção"
                    className="flex items-center gap-1 text-xs text-brand-400 hover:text-brand-300 transition-colors focus:outline-none focus-visible:underline"
                  >
                    <Plus className="w-3 h-3" aria-hidden="true" /> Seção
                  </button>
                </div>

                <div className="flex flex-col gap-4">
                  {sections.map((sec, secIdx) => (
                    <div key={secIdx} className="border border-white/[0.06] rounded-lg p-3 flex flex-col gap-2">
                      <input
                        type="text"
                        value={sec.title}
                        onChange={(e) => atualizarSection(secIdx, e.target.value)}
                        maxLength={24}
                        placeholder={`Título da seção ${secIdx + 1} (opcional)`}
                        aria-label={`Título da seção ${secIdx + 1}`}
                        className="w-full bg-surface-2 border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
                      />

                      {sec.rows.map((row, rowIdx) => (
                        <div key={row.id} className="flex items-start gap-2 pl-2 border-l border-white/[0.08]">
                          <div className="flex-1 flex flex-col gap-1">
                            <input
                              type="text"
                              value={row.title}
                              onChange={(e) => atualizarRow(secIdx, rowIdx, "title", e.target.value)}
                              maxLength={24}
                              placeholder="Título do item"
                              aria-label={`Título do item ${rowIdx + 1} da seção ${secIdx + 1}`}
                              className="w-full bg-surface-2 border border-white/[0.08] rounded px-2 py-1 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
                            />
                            <input
                              type="text"
                              value={row.description}
                              onChange={(e) => atualizarRow(secIdx, rowIdx, "description", e.target.value)}
                              maxLength={72}
                              placeholder="Descrição (opcional)"
                              aria-label={`Descrição do item ${rowIdx + 1}`}
                              className="w-full bg-surface-2 border border-white/[0.08] rounded px-2 py-1 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
                            />
                          </div>
                          {sec.rows.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removerRow(secIdx, rowIdx)}
                              aria-label={`Remover item ${rowIdx + 1}`}
                              className="text-ink-5 hover:text-red-400 mt-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded p-0.5"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}

                      {totalRowsAtual < 10 && (
                        <button
                          type="button"
                          onClick={() => adicionarRow(secIdx)}
                          aria-label={`Adicionar item à seção ${secIdx + 1}`}
                          className="flex items-center gap-1 text-xs text-ink-4 hover:text-brand-400 transition-colors focus:outline-none focus-visible:underline mt-1 self-start"
                        >
                          <Plus className="w-3 h-3" aria-hidden="true" /> Item
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {erro && (
            <p className="text-xs text-red-400" role="alert">{erro}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-ink-3 hover:text-ink-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded px-3 py-1.5"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleEnviar}
            disabled={enviando}
            aria-label="Enviar mensagem interativa"
            className="flex items-center gap-1.5 text-xs font-medium bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg px-4 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            {enviando
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
              : <Send className="w-3.5 h-3.5" aria-hidden="true" />}
            Enviar
          </button>
        </div>
      </div>
    </div>
  );
}
